import React, { useState, useEffect } from 'react';
import { Heart, Plus, X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { creatorAPI, walletAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

export default function TipMenu({ roomId, isCreator, onTipSent }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [sending, setSending] = useState(null);

  useEffect(() => {
    creatorAPI.getTipMenu(roomId).then(r => setItems(r.data.tip_menu || [])).catch(() => {});
  }, [roomId]);

  const addItem = () => {
    if (!newLabel.trim() || !newAmount) return;
    const updated = [...items, { label: newLabel, amount: parseFloat(newAmount), emoji: newEmoji || '' }];
    setItems(updated);
    setNewLabel(''); setNewAmount(''); setNewEmoji('');
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const saveMenu = async () => {
    try {
      await creatorAPI.setTipMenu(roomId, items);
      toast.success('Tip menu saved');
      setEditing(false);
    } catch (e) { toast.error('Failed to save'); }
  };

  const sendTip = async (item) => {
    setSending(item.label);
    try {
      await creatorAPI.tip(roomId, item.amount);
      toast.success(`Sent ${item.emoji} ${item.label} - $${item.amount}!`);
      onTipSent?.(item.amount);
    } catch (e) { toast.error(e.response?.data?.detail || 'Tip failed'); }
    finally { setSending(null); }
  };

  if (items.length === 0 && !isCreator) return null;

  return (
    <div className="border-t border-zinc-800 bg-zinc-900/50" data-testid="tip-menu">
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-xs font-bold text-rose-400 flex items-center gap-1.5">
          <Sparkles size={12} /> Tip Menu
        </span>
        {isCreator && (
          <Button onClick={() => setEditing(!editing)} variant="ghost" size="sm" className="text-zinc-400 text-xs h-6 px-2" data-testid="edit-tip-menu-btn">
            {editing ? 'Done' : 'Edit'}
          </Button>
        )}
      </div>

      {/* Viewer: clickable tip buttons */}
      {!editing && items.length > 0 && (
        <div className="px-3 pb-3 flex flex-wrap gap-1.5">
          {items.map((item, idx) => (
            <motion.button
              key={idx}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isCreator || sending === item.label}
              onClick={() => sendTip(item)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 border border-zinc-700 hover:border-rose-500/50 hover:bg-rose-500/10 text-xs transition-all disabled:opacity-50"
              data-testid={`tip-menu-item-${idx}`}
            >
              {item.emoji && <span>{item.emoji}</span>}
              <span className="text-zinc-300">{item.label}</span>
              <span className="text-rose-400 font-bold">${item.amount}</span>
            </motion.button>
          ))}
        </div>
      )}

      {/* Creator: edit mode */}
      <AnimatePresence>
        {editing && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3 space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-zinc-300">
                  <span>{item.emoji}</span>
                  <span className="flex-1">{item.label}</span>
                  <span className="text-rose-400 font-bold">${item.amount}</span>
                  <button onClick={() => removeItem(idx)} className="text-zinc-500 hover:text-red-400"><X size={12} /></button>
                </div>
              ))}
              <div className="flex items-center gap-1.5">
                <Input value={newEmoji} onChange={e => setNewEmoji(e.target.value)} placeholder="icon" className="w-10 h-7 bg-zinc-800 border-zinc-700 text-white text-xs px-1 text-center" />
                <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label" className="flex-1 h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="tip-menu-label-input" />
                <Input type="number" value={newAmount} onChange={e => setNewAmount(e.target.value)} placeholder="$" className="w-14 h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="tip-menu-amount-input" />
                <Button onClick={addItem} size="sm" className="h-7 px-2 bg-zinc-700 text-white rounded text-xs"><Plus size={12} /></Button>
              </div>
              <Button onClick={saveMenu} size="sm" className="w-full h-7 bg-rose-500 text-white text-xs font-bold rounded" data-testid="save-tip-menu-btn">Save Menu</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
